import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Check, CheckCheck, Wrench, ShoppingCart, Info, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const API_URL = window.location.origin + '/api';

const TYPE_ICONS = {
  wo_assigned: Wrench,
  wo_completed: Check,
  purchase_approved: ShoppingCart,
  warning: AlertTriangle,
  info: Info,
};

const TYPE_COLORS = {
  wo_assigned: 'text-blue-500',
  wo_completed: 'text-emerald-500',
  purchase_approved: 'text-purple-500',
  warning: 'text-amber-500',
  info: 'text-gray-500',
};

export function NotificationCenter() {
  const { getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const panelRef = useRef(null);

  // Dynamically calculate max height based on actual position in viewport
  useEffect(() => {
    if (open && panelRef.current) {
      const measure = () => {
        if (!panelRef.current) return;
        const rect = panelRef.current.getBoundingClientRect();
        const vh = window.visualViewport?.height || document.documentElement.clientHeight;
        const available = vh - rect.top - 16;
        // Cap at 600px to prevent excessively tall panels
        const maxH = Math.max(250, Math.min(available, 600));
        panelRef.current.style.maxHeight = `${maxH}px`;
      };
      requestAnimationFrame(measure);
      window.addEventListener('resize', measure);
      if (window.visualViewport) window.visualViewport.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
        if (window.visualViewport) window.visualViewport.removeEventListener('resize', measure);
      };
    }
  }, [open]);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/count`, { headers: getAuthHeaders() });
      setUnreadCount(res.data?.unread || 0);
    } catch { /* silent */ }
  }, [getAuthHeaders]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/notifications?limit=20`, { headers: getAuthHeaders() });
      setNotifications(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API_URL}/notifications/read-all`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-white/70" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="fixed right-4 w-80 bg-white rounded-xl shadow-2xl border z-[100] flex flex-col overflow-hidden" style={{ top: '64px' }} data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b bg-gray-50 flex-shrink-0 rounded-t-xl">
            <h3 className="font-semibold text-sm text-foreground">Notificacoes</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead} data-testid="mark-all-read">
                <CheckCheck className="w-3 h-3 mr-1" /> Marcar todas
              </Button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <p className="text-center text-muted-foreground text-sm py-6">Carregando...</p>
            ) : notifications.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-6">Nenhuma notificacao</p>
            ) : (
              notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const iconColor = TYPE_COLORS[n.type] || 'text-gray-500';
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => !n.is_read && markRead(n.id)}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`mt-0.5 ${iconColor}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.is_read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                        {n.title}
                      </p>
                      {n.message && <p className="text-xs text-muted-foreground truncate">{n.message}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: ptBR }) : ''}
                      </p>
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
